<template>
  <div
    class="w-full h-full flex flex-col pt-4 px-3 pb-0! tablet-h:p-[14px] tablet-h:pr-[64px] desktop:pr-[14px] relative"
    :style="componentStyles"
  >
    <!-- Header -->
    <div class="tablet-h:ml-10 mb-[15px]">
      <Breadcrumb
        class="text-[18px] leading-[20px] tracking-[-0.25px]"
        :path="breadcrumbPath"
        :iconSize="22"
        @location="handleBreadcrumbClick"
      />

      <div class="font-secondary text-[14px] mb-6">
        Check for infringement with
        <span
          class="w-fit bg-linear-to-r from-yellow-500 via-[#F84600] to-[#CE489A] bg-clip-text text-transparent font-medium"
        >
          Dacr AI
        </span>
      </div>

      <div
        v-if="!isGroupView"
        class="flex items-center gap-x-[15px] mb-5 tablet-h:mb-4"
      >
        <div
          class="w-fit min-w-[70px] tablet-h:w-[168px] h-[64px] border rounded-md px-2.5 py-2 cursor-pointer transition-colors"
          :class="
            activeTab === null
              ? 'border-green-500 bg-green-200/25 text-green-900'
              : 'border-gray-200/75 bg-white hover:border-gray-300'
          "
          @click="activeTab = null"
        >
          <div class="font-secondary text-[14px] leading-[22px]">All</div>
          <div class="font-secondary text-[18px] leading-[22px]">
            {{ !hasLoaded ? '-' : total }}
          </div>
        </div>

        <div
          class="w-fit min-w-[100px] tablet-h:w-[168px] h-[64px] border rounded-md px-2.5 py-2 cursor-pointer transition-colors"
          :class="
            activeTab === InfringementLabel.Flag
              ? 'border-green-500 bg-green-200/25 text-green-900'
              : 'border-gray-200/75 bg-white hover:border-gray-300'
          "
          @click="activeTab = InfringementLabel.Flag"
        >
          <div class="font-secondary text-[14px] leading-[22px]">Flagged</div>
          <div class="font-secondary text-[18px] leading-[22px]">
            {{ !hasLoaded ? '-' : totalFlags }}
          </div>
        </div>

        <div
          class="w-fit min-w-[100px] tablet-h:w-[168px] h-[64px] border rounded-md px-2.5 py-2 cursor-pointer transition-colors"
          :class="
            activeTab === InfringementLabel.Report
              ? 'border-green-500 bg-green-200/25 text-green-900'
              : 'border-gray-200/75 bg-white hover:border-gray-300'
          "
          @click="activeTab = InfringementLabel.Report"
        >
          <div class="font-secondary text-[14px] leading-[22px]">Reports</div>
          <div class="font-secondary text-[18px] leading-[22px]">
            {{ cachedTotalReports == null && loading ? '-' : totalReports }}
          </div>
        </div>
      </div>

      <div
        class="w-full flex flex-row items-center justify-between gap-y-[14px] mb-5"
      >
        <UiButton
          icon="Plus"
          primary
          :disabled="!hasLoaded"
          @click="handleCreateReportClick"
        >
          Infringement report
        </UiButton>

        <Skeleton v-if="!hasLoaded" class="w-[160px] h-[32px]" />
        <div
          v-else
          class="w-fit h-fit font-secondary text-[14px] leading-[18px] px-[9px] py-1.5 border border-gray-200/75 bg-gray-200/13 rounded-lg text-gray-800 flex items-center gap-x-1.5 cursor-pointer transition-colors hover:border-gray-300 hover:bg-gray-200/25"
          @click="handleBuyCreditsClick"
        >
          <span class="text-gray-500 font-secondary"> Report credits </span>
          <div class="font-secondary">{{ userStore.user?.reportCredits }}</div>
          <Icon name="ChevronUp" :size="10" class="rotate-90 stroke-gray-800" />
        </div>
      </div>
    </div>

    <div
      v-if="device.isMobile.value || device.isTabletVertical.value"
      class="tablet-h:hidden border-t border-[#EFEFEF] h-full flex flex-col overflow-hidden"
    >
      <div
        v-if="hasLoaded && userStore.user?.isCreatingReport"
        class="relative pointer-events-none py-4"
      >
        <div class="border--gradient w-full h-0.5 absolute top-0 left-0"></div>

        <div class="font-secondary text-[14px] leading-[18px] text--gradient">
          Creating report(s)
        </div>
      </div>

      <NoInfringements
        v-if="!loading && !infringements.length"
        @create="handleCreateReportClick"
      />

      <div v-else class="overflow-auto" @scroll="handleScroll">
        <template v-if="loading && !infringements.length">
          <div
            v-for="n in 5"
            :key="n"
            class="w-full h-[100px] pr-5 py-2.5 border-t border-[#EFEFEF] flex flex-col justify-center gap-y-2"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-x-1.5">
                <Skeleton class="w-[40px] h-[14px]" />
                <Skeleton class="w-[100px] h-2" />
              </div>
              <Skeleton class="w-[60px] h-[22px]" />
            </div>
            <div class="flex items-center gap-x-3">
              <Skeleton class="min-w-[24px] max-w-[24px] h-[24px]" />
              <Skeleton class="w-[140px] h-[14px]" />
            </div>
            <div class="flex items-center gap-x-3">
              <Skeleton class="min-w-[24px] max-w-[24px] h-[24px]" />
              <Skeleton class="w-[120px] h-[14px]" />
            </div>
          </div>
        </template>
        <template v-else>
          <InfringementListItem
            v-for="infringement in infringements"
            :key="infringement.id"
            :infringement="infringement"
          />
        </template>
      </div>
    </div>

    <div class="hidden tablet-h:flex flex-col pl-[30px] flex-1 overflow-hidden">
      <InfringementListTable
        :infringements="infringements"
        :processing="hasLoaded && userStore.user?.isCreatingReport"
        :loading="loading && !infringements.length"
        :empty="!loading && !infringements.length"
        :sort="activeSort"
        @scroll="handleScroll"
        @create="handleCreateReportClick"
        @sort="handleSortChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import Breadcrumb from '@/shared/components/Breadcrumb.vue'
import InfringementListItem from '@/features/infringement/components/InfringementListItem.vue'
import NoInfringements from '@/features/infringement/components/NoInfringements.vue'
import InfringementListTable from '@/features/infringement/components/InfringementListTable.vue'
import { Dialogs } from '@/shared/dialogs/types'
import { useDialog } from '@/shared/dialogs/useDialog'
import { graphql } from '@/shared/graphql/router'
import { InfringementLabel } from '@/shared/graphql/router/graphql'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'
import { useAvailableHeight } from '@/shared/composables/useAvailableHeight'
import { Float, Icon, Radio, Skeleton, UiButton } from '@dacr/ui'
import { useQuery } from '@vue/apollo-composable'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const device = useDevice()
const { availableHeight } = useAvailableHeight()
const userStore = useUserStore()
const { open } = useDialog()
const route = useRoute()
const router = useRouter()

const isGroupView = computed(() => route.name === 'InfringementGroupView')

const groupFilter = computed(() => {
  if (!isGroupView.value) return undefined
  return {
    id: route.params.groupId as string,
    timestamp: Number(route.params.timestamp),
  }
})

const breadcrumbPath = computed(() =>
  isGroupView.value
    ? ['Infringment analysis', 'Flagged works']
    : ['Infringment analysis']
)

const activeTab = ref<InfringementLabel | null>(null)

type SortField = 'TIMESTAMP' | 'SCORE'
type SortDirection = 'ASC' | 'DESC'

type InfringementSort = {
  field: SortField
  direction: SortDirection
}

const sortOptions: {
  label: string
  field: SortField
  direction: SortDirection
}[] = [
  { label: 'Date (newest first)', field: 'TIMESTAMP', direction: 'DESC' },
  {
    label: 'Infringement match (highest)',
    field: 'SCORE',
    direction: 'DESC',
  },
]

const activeSort = ref<InfringementSort>({
  field: 'TIMESTAMP',
  direction: 'DESC',
})

const activeSortLabel = computed(() => {
  const match = sortOptions.find(
    (opt) =>
      opt.field === activeSort.value.field &&
      opt.direction === activeSort.value.direction
  )
  return match?.label ?? 'Sort'
})

const sortOptionValue = computed(() => activeSortLabel.value)

const handleSortChange = (sort: InfringementSort) => {
  activeSort.value = sort
}

const handleSortOptionSelect = (opt: (typeof sortOptions)[number]) => {
  activeSort.value = { field: opt.field, direction: opt.direction }
}

const { result, loading, fetchMore } = useQuery(
  graphql(`
    query InfringementList($input: InfringementsInput) {
      infringements(input: $input) {
        totalFlags
        totalReports
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            label
            timestamp
            copyrights {
              id
              name
              asset {
                previewImageUrl
              }
              type
            }
            infringingFile {
              id
              name
              asset {
                previewImageUrl
              }
              type
            }
            matches {
              score
              level
              breakdown {
                level
                score
                type
              }
            }
            report {
              id
              status
            }
          }
        }
      }
    }
  `),
  () => ({
    input: {
      first: 20,
      sort: activeSort.value,
      filterBy: {
        ...(activeTab.value && { type: activeTab.value }),
        ...(groupFilter.value && { group: groupFilter.value }),
      },
    },
  }),
  {
    clientId: 'router',
    ...(isGroupView.value && { fetchPolicy: 'no-cache' }),
  }
)

const hasLoaded = ref(false)
const cachedTotalFlags = ref<number>()
const cachedTotalReports = ref<number>()

watch(
  result,
  (value) => {
    if (value) {
      hasLoaded.value = true

      if (activeTab.value === null) {
        cachedTotalFlags.value = value.infringements.totalFlags
        cachedTotalReports.value = value.infringements.totalReports
      }
    }
  },
  { immediate: true }
)

const infringements = computed(
  () => result.value?.infringements.edges.map((edge) => edge.node) ?? []
)

const componentStyles = computed(() => ({
  height: `${availableHeight.value}px`,
}))

const totalFlags = computed(
  () => cachedTotalFlags.value ?? result.value?.infringements.totalFlags
)

const totalReports = computed(
  () => cachedTotalReports.value ?? result.value?.infringements.totalReports
)

const total = computed(
  () => (totalFlags.value || 0) + (totalReports.value || 0)
)

const credits = computed(() => userStore.user?.reportCredits || 0)

const fetchNextPage = () => {
  const pageInfo = result.value?.infringements.pageInfo
  if (pageInfo?.hasNextPage && !loading.value) {
    fetchMore({
      variables: {
        input: {
          first: 20,
          after: pageInfo.endCursor,
          sort: activeSort.value,
          filterBy: {
            ...(activeTab.value && { type: activeTab.value }),
            ...(groupFilter.value && { group: groupFilter.value }),
          },
        },
      },
      updateQuery: (previousData, { fetchMoreResult: newData }) => {
        if (!newData) return previousData

        return {
          __typename: 'Query' as const,
          infringements: {
            ...previousData.infringements,
            edges: [
              ...previousData.infringements.edges,
              ...newData.infringements.edges,
            ],
            pageInfo: newData.infringements.pageInfo,
          },
        }
      },
    })
  }
}

const handleScroll = (event: Event) => {
  const element = event.target as HTMLElement
  const elementHeight = element.scrollHeight
  const scrollPos = element.clientHeight + element.scrollTop

  if (scrollPos / elementHeight >= 0.5) {
    fetchNextPage()
  }
}

const isMobile = computed(
  () => device.isMobile.value || device.isTabletVertical.value
)

const handleBuyCreditsClick = () => {
  open(Dialogs.BuyReportCredits, {
    props: isMobile.value ? { openCreateReport: true } : undefined,
  })
}

const handleCreateReportClick = () => {
  if (!result.value?.infringements.totalReports) {
    return open(Dialogs.InfringementReport)
  }

  if (credits.value) {
    return open(Dialogs.CreateReport)
  }

  open(Dialogs.BuyReportCredits, {
    props: { openCreateReport: true },
  })
}

const handleBreadcrumbClick = (path: string[]) => {
  if (path.length === 1) {
    router.push('/infringement')
  }
}
</script>

<style scoped>
.text--gradient {
  width: fit-content;
  background: linear-gradient(to right, #1bbedb, #ffa20b, #f84600, #db3199);
  color: transparent;
  background-clip: text;
  background-size: 300% 300%;
  animation: cycle 6s ease infinite;
}

.border--gradient {
  background: linear-gradient(to right, #1bbedb, #ffa20b, #f84600, #db3199);
  background-size: 300% 300%;
  animation: cycle 6s ease infinite;
}

.border--gradient::before {
  width: 100%;
  height: 1px;
  content: '';
  position: absolute;
  top: 5px;
  left: 0;
  background: linear-gradient(to right, #1bbedb, #ffa20b, #f84600, #db3199);
  background-size: 300% 300%;
  filter: blur(5px);
  animation: cycle 6s ease infinite;
}

@keyframes cycle {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}
</style>
